import React, { useState, useEffect, useCallback, useMemo } from 'react';
// jspdf is lazy-loaded inside exportPDF — see Sales.js for rationale.
import { fetchWithAuth } from '../../utils/api';
import { useToast } from '../../contexts/ToastContext';
import { formatNumber, formatWeight, buildCSV, getCurrentLocalDatetime } from '../../utils/helpers';
import RowsPerPageSelector from '../common/RowsPerPageSelector';
import Pagination from '../common/Pagination';
import SortSelector from '../common/SortSelector';
import ConfirmationModal from '../modals/ConfirmationModal';
import HarvestAnalytics from '../analytics/HarvestAnalytics';
import { 
  PlusCircle, 
  Edit, 
  X,
  Users
} from 'lucide-react';

function HarvestAgents({ user, setView, onSaleLinkClick, targetEmployeeName, clearTargetEmployee, targetSaleTally, clearTargetSaleTally, targetHarvestId, clearTargetHarvest, handleLogout }) {
  const toast = useToast();
  const [agents, setAgents] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [sales, setSales] = useState([]);
  const [locations, setLocations] = useState([]);
  const [error, setError] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [showRecordModal, setShowRecordModal] = useState(false);
  const [editingAgent, setEditingAgent] = useState(null);
  const [viewingAgent, setViewingAgent] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, id: null });
  const [archiveConfirm, setArchiveConfirm] = useState({ isOpen: false, ids: [], archived: false });
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [layoutMode, setLayoutMode] = useState(window.innerWidth < 768 ? 'cards' : 'table');
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({ key: 'date', direction: 'desc' });
  const [selectedIds, setSelectedIds] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (targetEmployeeName) {
      setFilters(prev => ({ ...prev, employeeName: targetEmployeeName }));
      setShowFilters(true);
      if (clearTargetEmployee) clearTargetEmployee();
    }
  }, [targetEmployeeName, clearTargetEmployee]);

  useEffect(() => {
    if (targetSaleTally) {
      setFilters(prev => ({ ...prev, saleTally: targetSaleTally }));
      setShowFilters(true);
      if (clearTargetSaleTally) clearTargetSaleTally();
    }
  }, [targetSaleTally, clearTargetSaleTally]);
  
  // Filter States
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    employeeName: '',
    buyer: '',
    heads: { op: 'eq', val: '' },
    kilos: { op: 'eq', val: '' },
    location: '',
    saleTally: '',
    archived: 'unarchived'
  });

  const [newAgent, setNewAgent] = useState({
    date: getCurrentLocalDatetime(),
    employee_id: '',
    employee_search: '',
    buyer: '',
    heads: '',
    kilos: '',
    sale_id: '',
    sale_search: ''
  });

  // Each fetcher accepts an optional AbortSignal so the mount-effect can
  // cancel in-flight requests on unmount / dep change.
  const fetchAgents = useCallback((signal) => {
    return fetchWithAuth('/harvest_agents', { signal }, user, handleLogout)
      .then(res => res.json())
      .then(data => setAgents(data || []))
      .catch(err => { if (err?.name !== 'AbortError') console.error(err); });
  }, [user, handleLogout]);

  const fetchEmployees = useCallback((signal) => {
    return fetchWithAuth('/employees', { signal }, user, handleLogout)
      .then(res => res.json())
      .then(data => setEmployees(data || []))
      .catch(err => { if (err?.name !== 'AbortError') console.error(err); });
  }, [user, handleLogout]);

  const fetchSales = useCallback((signal) => {
    return fetchWithAuth('/sales', { signal }, user, handleLogout)
      .then(res => res.json())
      .then(data => setSales(data || []))
      .catch(err => { if (err?.name !== 'AbortError') console.error(err); });
  }, [user, handleLogout]);

  const fetchLocations = useCallback((signal) => {
    return fetchWithAuth('/locations', { signal }, user, handleLogout)
      .then(res => res.json())
      .then(data => setLocations(data || []))
      .catch(err => { if (err?.name !== 'AbortError') console.error(err); });
  }, [user, handleLogout]);

  useEffect(() => {
    const controller = new AbortController();
    setIsLoading(true);
    Promise.allSettled([
      fetchAgents(controller.signal),
      fetchEmployees(controller.signal),
      fetchSales(controller.signal),
      fetchLocations(controller.signal),
    ]).then(() => setIsLoading(false));
    return () => controller.abort();
  }, [fetchAgents, fetchEmployees, fetchSales, fetchLocations]);

  useEffect(() => {
    if (targetHarvestId && agents.length > 0) {
      const target = agents.find(a => a.id === targetHarvestId);
      if (target) {
        handleViewClick(target);
      }
      if (clearTargetHarvest) clearTargetHarvest(null);
    }
  }, [targetHarvestId, agents, clearTargetHarvest]);

  const handleInputChange = (e, isEditing = false) => {
    const { name, value, type, checked } = e.target;
    const val = type === 'checkbox' ? checked : value;
    const setter = isEditing ? setEditingAgent : setNewAgent;
    const current = isEditing ? editingAgent : newAgent;

    if (name === 'sale_search') {
      const selectedSale = sales.find(s => `${s.tally_sheet_number} - ${s.buyer}` === value);
      setter({ ...current, sale_id: selectedSale ? selectedSale.id : '', sale_search: value });
    } else if (name === 'employee_search') {
      const selectedEmp = employees.find(emp => `${emp.first_name} ${emp.last_name}` === value);
      setter({ ...current, employee_id: selectedEmp ? selectedEmp.id : '', employee_search: value });
    } else {
      setter({ ...current, [name]: val });
    }
  };
  const handleFilterChange = (field, value, subField = null) => {
    if (subField) {
      setFilters(prev => ({ ...prev, [field]: { ...prev[field], [subField]: value } }));
    } else {
      setFilters(prev => ({ ...prev, [field]: value }));
    }
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedIds(paginatedAgents.map(a => a.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectOne = (id) => {
    setSelectedIds(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const handleArchive = (ids, archivedStatus) => {
    if (!ids || ids.length === 0) return;
    setArchiveConfirm({ isOpen: true, ids, archived: archivedStatus });
  };

  const performArchive = () => {
    const { ids, archived: archivedStatus } = archiveConfirm;
    setArchiveConfirm({ isOpen: false, ids: [], archived: false });

    fetchWithAuth('/archive', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        type: 'harvest_agents',
        ids: ids,
        archived: archivedStatus
      })
    }, user, handleLogout)
    .then(res => {
      if (!res.ok) throw new Error('Failed to update archive status');
      fetchAgents();
      setSelectedIds([]);
      toast.success(archivedStatus ? 'Records archived.' : 'Records restored.');
    })
    .catch(err => toast.error(err.message));
  };

  // Reset to first page when filters, rows per page or sort changes
  useEffect(() => {
    setCurrentPage(1);
  }, [filters, rowsPerPage, sortConfig]);

  // Filter Logic
  const filteredAgents = useMemo(() => {
    return agents.filter(a => {
      // Date Range
      if (filters.startDate) {
        const agentDate = new Date(a.date).toISOString().split('T')[0];
        if (agentDate < filters.startDate) return false;
      }
      if (filters.endDate) {
        const agentDate = new Date(a.date).toISOString().split('T')[0];
        if (agentDate > filters.endDate) return false;
      }

      // Employee
      if (filters.employeeName && (!a.employee_name || !a.employee_name.toLowerCase().includes(filters.employeeName.toLowerCase()))) return false;

      // Buyer
      if (filters.buyer && !a.buyer.toLowerCase().includes(filters.buyer.toLowerCase())) return false;

      // Location
      if (filters.location && (!a.location_name || !a.location_name.toLowerCase().includes(filters.location.toLowerCase()))) return false;

      // Sale Tally
      if (filters.saleTally && (!a.sale_tally || !a.sale_tally.toLowerCase().includes(filters.saleTally.toLowerCase()))) return false;

      // Archive status
      if (filters.archived === 'unarchived' && a.archived) return false;
      if (filters.archived === 'archived' && !a.archived) return false;

      // Numeric Filters Helper
      const checkNumeric = (val, filter) => {
        if (filter.val === '') return true;
        const numVal = parseFloat(filter.val);
        if (filter.op === 'gt') return val > numVal;
        if (filter.op === 'lt') return val < numVal;
        return val === numVal;
      };

      if (!checkNumeric(a.heads, filters.heads)) return false;
      if (!checkNumeric(a.kilos, filters.kilos)) return false;

      return true;
    });
  }, [agents, filters]);

  // Sorting Logic
  const sortedAgents = useMemo(() => {
    let sortableItems = [...filteredAgents];
    if (sortConfig.key !== null) {
      sortableItems.sort((a, b) => {
        let aVal = a[sortConfig.key];
        let bVal = b[sortConfig.key];

        // Handle special cases
        if (sortConfig.key === 'employee') {
          aVal = (a.employee_name || '').toLowerCase();
          bVal = (b.employee_name || '').toLowerCase();
        } else if (sortConfig.key === 'sale_tally') {
          aVal = (a.sale_tally || '').toLowerCase();
          bVal = (b.sale_tally || '').toLowerCase();
        } else if (sortConfig.key === 'location') {
          aVal = (a.location_name || '').toLowerCase();
          bVal = (b.location_name || '').toLowerCase();
        } else if (typeof aVal === 'string') {
          aVal = aVal.toLowerCase();
          bVal = bVal.toLowerCase();
        }

        if (aVal < bVal) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aVal > bVal) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableItems;
  }, [filteredAgents, sortConfig]);

  const paginatedAgents = useMemo(() => {
    if (rowsPerPage === 'all') return sortedAgents;
    const start = (currentPage - 1) * rowsPerPage;
    return sortedAgents.slice(start, start + rowsPerPage);
  }, [sortedAgents, currentPage, rowsPerPage]);

  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const totalHeads = filteredAgents.reduce((sum, a) => sum + a.heads, 0);
  const totalKilos = filteredAgents.reduce((sum, a) => sum + a.kilos, 0);

  const exportCSV = () => {
    const headers = ['Date', 'Employee', 'Buyer', 'Heads', 'Weight (Kilos)', 'Linked Sale (Tally #)', 'Location'];
    const rows = sortedAgents.map(a => [
      new Date(a.date).toLocaleDateString(),
      a.employee_name,
      a.buyer,
      a.heads,
      a.kilos,
      a.sale_tally,
      a.location_name || ''
    ]);

    // Add totals row
    const totalsRow = ['TOTALS', '', '', totalHeads, totalKilos, '', ''];
    const csvContent = buildCSV([headers, ...rows, totalsRow]);

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `harvest_agents_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportPDF = async () => {
    const [{ default: jsPDF }, { default: autoTable }] = await Promise.all([
      import('jspdf'),
      import('jspdf-autotable'),
    ]);
    const doc = new jsPDF('landscape');
    doc.text("Harvest Agents Report", 14, 15);
    
    const tableColumn = ["Date", "Employee", "Buyer", "Heads", "Weight (Kilos)", "Linked Sale", "Location"];
    const tableRows = sortedAgents.map(a => [
      new Date(a.date).toLocaleDateString(),
      a.employee_name,
      a.buyer,
      formatNumber(a.heads),
      formatWeight(a.kilos),
      a.sale_tally,
      a.location_name || '-'
    ]);

    autoTable(doc, {
      head: [tableColumn],
      body: tableRows,
      foot: [['TOTALS', '', '', formatNumber(totalHeads), formatWeight(totalKilos), '', '']],
      startY: 20,
      theme: 'grid',
      headStyles: { fillColor: [44, 62, 80] },
      styles: { fontSize: 8 },
      footStyles: { fillColor: [248, 249, 250], textColor: [44, 62, 80], fontStyle: 'bold' }
    });
    doc.save(`harvest_agents_${new Date().toISOString().split('T')[0]}.pdf`);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    setError('');

    const isEditing = !!editingAgent;
    const agentData = isEditing ? editingAgent : newAgent;

    if (!agentData.sale_id) {
      setError("Please select a valid sale tally from the list.");
      return;
    }
    if (!agentData.employee_id) {
      setError("Please select a valid employee from the list.");
      return;
    }

    // Validation against sale total
    const selectedSale = sales.find(s => s.id === parseInt(agentData.sale_id));
    const totalAllowed = selectedSale.number_of_heads;
    const existingHeads = agents.filter(a => a.sale_id === selectedSale.id && (!isEditing || a.id !== editingAgent.id)).reduce((sum, a) => sum + a.heads, 0);
    const newHeads = parseInt(agentData.heads);
    const remaining = totalAllowed - existingHeads;

    if (newHeads > remaining) {
      setError(`Validation Error: This sale record only has ${remaining} heads remaining (Total: ${totalAllowed}, Already recorded: ${existingHeads}). You cannot record ${newHeads} heads.`);
      return;
    }

    const endpoint = isEditing ? '/harvest_agents/update' : '/harvest_agents/create';
    const isoDate = new Date(agentData.date).toISOString();

    setIsSubmitting(true);
    fetchWithAuth(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        ...agentData,
        date: isoDate,
        employee_id: parseInt(agentData.employee_id),
        heads: newHeads,
        kilos: parseFloat(agentData.kilos),
        sale_id: parseInt(agentData.sale_id),
        archived: !!agentData.archived
      })
    }, user, handleLogout)
    .then(async res => {
      if (!res.ok) throw new Error(await res.text());
      return res.json();
    })
    .then(() => {
      fetchAgents();
      setShowRecordModal(false);
      setEditingAgent(null);
      setNewAgent({ date: getCurrentLocalDatetime(), employee_id: '', employee_search: '', buyer: '', heads: '', kilos: '', sale_id: '', sale_search: '' });
    })
    .catch(err => setError(err.message))
    .finally(() => setIsSubmitting(false));
  };

  const handleViewClick = (agent) => {
    setViewingAgent(agent);
    setError('');
    setEditingAgent(null);
  };

  const handleEditClick = (agent) => {
    // Format date for datetime-local input
    const localDate = new Date(agent.date);
    localDate.setMinutes(localDate.getMinutes() - localDate.getTimezoneOffset());
    const dateStr = localDate.toISOString().slice(0, 16);

    setEditingAgent({
      ...agent,
      date: dateStr,
      employee_search: agent.employee_name,
      sale_search: `${agent.sale_tally} - ${agent.buyer}`,
      archived: agent.archived || false
    });
    setError('');
    setViewingAgent(null);
    setShowRecordModal(true);
  };

  const handleDelete = (id) => {
    setDeleteConfirm({ isOpen: true, id });
  };

  const confirmDelete = () => {
    const id = deleteConfirm.id;
    setError('');
    fetchWithAuth(`/harvest_agents/delete?id=${id}`, {
      method: 'POST'
    }, user, handleLogout)
    .then(async res => {
      if (!res.ok) throw new Error(await res.text());
      fetchAgents();
      setDeleteConfirm({ isOpen: false, id: null });
    })
    .catch(err => {
      setError(err.message);
      setDeleteConfirm({ isOpen: false, id: null });
    });
  };

  return (
    <div className="harvest-view">
      <div className="view-header">
        <h2>Harvest Agents</h2>
        <div className="view-actions">
          <button className="secondary-btn no-print" onClick={() => setShowFilters(!showFilters)}>
            {showFilters ? 'Hide Filters' : 'Show Filters'}
          </button>
          <button className="submit-btn no-print" onClick={() => setShowRecordModal(true)}>
            Add Harvest Record
          </button>
          <button className="secondary-btn no-print" onClick={() => setShowAnalytics(!showAnalytics)}>
            {showAnalytics ? 'Hide Analytics' : 'Show Analytics'}
          </button>
          <button className="action-btn no-print" onClick={exportCSV}>
            Download CSV
          </button>
          <button className="action-btn no-print" onClick={exportPDF}>
            Download PDF
          </button>
        </div>
      </div>

      {showFilters && (
        <div className="filter-panel">
          <h4>Filters</h4>
          <div className="filter-grid">
            <div className="filter-group">
              <label>Date Range</label>
              <div className="date-range-inputs">
                <input type="date" value={filters.startDate} onChange={(e) => handleFilterChange('startDate', e.target.value)} />
                <span>to</span>
                <input type="date" value={filters.endDate} onChange={(e) => handleFilterChange('endDate', e.target.value)} />
              </div>
            </div>

            <div className="filter-group">
              <label>Employee</label>
              <input placeholder="Search name..." value={filters.employeeName} onChange={(e) => handleFilterChange('employeeName', e.target.value)} />
            </div>

            <div className="filter-group">
              <label>Buyer</label>
              <input placeholder="Search buyer..." value={filters.buyer} onChange={(e) => handleFilterChange('buyer', e.target.value)} />
            </div>

            <div className="filter-group">
              <label>Location</label>
              <input 
                list="location-options"
                placeholder="Search location..." 
                value={filters.location} 
                onChange={(e) => handleFilterChange('location', e.target.value)} 
              />
              <datalist id="location-options">
                {locations.map(l => <option key={l.id} value={l.name} />)}
              </datalist>
            </div>

            <div className="filter-group">
              <label>Sale Tally #</label>
              <input placeholder="Search tally..." value={filters.saleTally} onChange={(e) => handleFilterChange('saleTally', e.target.value)} />
            </div>

            <div className="filter-group">
              <label>Archive Status</label>
              <select value={filters.archived} onChange={(e) => handleFilterChange('archived', e.target.value)}>
                <option value="unarchived">Unarchived Only</option>
                <option value="archived">Archived Only</option>
                <option value="all">All Records</option>
              </select>
            </div>

            <div className="filter-group">
              <label>Heads</label>
              <div className="numeric-filter">
                <select value={filters.heads.op} onChange={(e) => handleFilterChange('heads', e.target.value, 'op')}>
                  <option value="eq">=</option>
                  <option value="gt">&gt;</option>
                  <option value="lt">&lt;</option>
                </select>
                <input type="number" placeholder="Value" value={filters.heads.val} onChange={(e) => handleFilterChange('heads', e.target.value, 'val')} />
              </div>
            </div>

            <div className="filter-group">
              <label>Kilos</label>
              <div className="numeric-filter">
                <select value={filters.kilos.op} onChange={(e) => handleFilterChange('kilos', e.target.value, 'op')}>
                  <option value="eq">=</option>
                  <option value="gt">&gt;</option>
                  <option value="lt">&lt;</option>
                </select>
                <input type="number" placeholder="Value" value={filters.kilos.val} onChange={(e) => handleFilterChange('kilos', e.target.value, 'val')} />
              </div>
            </div>
          </div>
          <div className="filter-actions">
            <button onClick={() => setFilters({
              startDate: '', endDate: '', employeeName: '', buyer: '',
              heads: { op: 'eq', val: '' }, kilos: { op: 'eq', val: '' },
              location: '', saleTally: '', archived: 'unarchived'
            })} className="secondary-btn">Clear All Filters</button>
          </div>
        </div>
      )}

      {showAnalytics && (
        <HarvestAnalytics agents={filteredAgents} />
      )}

      <div className="list-controls">
        <div className="control-group">
          <RowsPerPageSelector value={rowsPerPage} onChange={setRowsPerPage} />
          {layoutMode === 'cards' && (
            <SortSelector 
              options={[
                { key: 'date', label: 'Date' },
                { key: 'employee', label: 'Employee' },
                { key: 'buyer', label: 'Buyer' },
                { key: 'heads', label: 'Heads' },
                { key: 'kilos', label: 'Weight' },
                { key: 'sale_tally', label: 'Linked Sale' },
                { key: 'location', label: 'Location' }
              ]} 
              sortConfig={sortConfig} 
              onSortChange={setSortConfig} 
            />
          )}
        </div>
        <div className="layout-toggle no-print">
          <button className={layoutMode === 'table' ? 'active' : ''} onClick={() => setLayoutMode('table')}>Table</button>
          <button className={layoutMode === 'cards' ? 'active' : ''} onClick={() => setLayoutMode('cards')}>Cards</button>
        </div>
      </div>

      {isLoading && <div className="loading-banner">Loading harvest records…</div>}

      {layoutMode === 'table' ? (
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th><input
                  type="checkbox"
                  ref={el => { if (el) el.indeterminate = selectedIds.length > 0 && selectedIds.length < paginatedAgents.length; }}
                  onChange={handleSelectAll}
                  checked={paginatedAgents.length > 0 && selectedIds.length === paginatedAgents.length}
                /></th>
                <th onClick={() => requestSort('date')} className="sortable">Date {sortConfig.key === 'date' && (sortConfig.direction === 'asc' ? '▲' : '▼')}</th>
                <th onClick={() => requestSort('employee')} className="sortable">Employee {sortConfig.key === 'employee' && (sortConfig.direction === 'asc' ? '▲' : '▼')}</th>
                <th onClick={() => requestSort('buyer')} className="sortable">Buyer {sortConfig.key === 'buyer' && (sortConfig.direction === 'asc' ? '▲' : '▼')}</th>
                <th onClick={() => requestSort('heads')} className="sortable text-right">Heads {sortConfig.key === 'heads' && (sortConfig.direction === 'asc' ? '▲' : '▼')}</th>
                <th onClick={() => requestSort('kilos')} className="sortable text-right">Weight {sortConfig.key === 'kilos' && (sortConfig.direction === 'asc' ? '▲' : '▼')}</th>
                <th onClick={() => requestSort('sale_tally')} className="sortable">Linked Sale {sortConfig.key === 'sale_tally' && (sortConfig.direction === 'asc' ? '▲' : '▼')}</th>
                <th onClick={() => requestSort('location')} className="sortable">Location {sortConfig.key === 'location' && (sortConfig.direction === 'asc' ? '▲' : '▼')}</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedAgents.map(a => (
                <tr key={a.id} onClick={() => handleViewClick(a)} className={`clickable-row ${a.archived ? 'archived-row' : ''} ${selectedIds.includes(a.id) ? 'selected-row' : ''}`}>
                  <td onClick={(e) => e.stopPropagation()}><input type="checkbox" checked={selectedIds.includes(a.id)} onChange={() => handleSelectOne(a.id)} /></td>
                  <td>{new Date(a.date).toLocaleDateString()} {a.archived && <span className="archived-tag">Archived</span>}</td>
                  <td>{a.employee_name}</td>
                  <td>{a.buyer}</td>
                  <td className="text-right">{formatNumber(a.heads)}</td>
                  <td className="text-right">{formatWeight(a.kilos)}</td>
                  <td onClick={(event) => { event.stopPropagation(); onSaleLinkClick(a.sale_id); }} className="sale-link">
                    {a.sale_tally}
                  </td>
                  <td>{a.location_name || '-'}</td>
                  <td onClick={(event) => event.stopPropagation()} className="row-actions">
                    <button className="action-btn sm" onClick={() => handleEditClick(a)}>Edit</button>
                    <button className="secondary-btn sm" onClick={() => handleArchive([a.id], !a.archived)}>{a.archived ? 'Restore' : 'Archive'}</button>
                    {user.role === 'admin' && (
                      <button className="cancel-btn sm" onClick={() => handleDelete(a.id)}>Del</button>
                    )}
                  </td>
                </tr>
              ))}
              <tr className="totals-row">
                <td colSpan="4">Totals</td>
                <td className="text-right highlight"><strong>{formatNumber(totalHeads)}</strong></td>
                <td className="text-right"><strong>{formatWeight(totalKilos)}</strong></td>
                <td colSpan="3"></td>
              </tr>
            </tbody>
          </table>
        </div>
      ) : (
        <div className="card-grid">
          {paginatedAgents.map(a => (
            <div key={a.id} className={`data-card ${a.archived ? 'archived-row' : ''} ${selectedIds.includes(a.id) ? 'selected-card' : ''}`} onClick={() => handleViewClick(a)}>
              <div className="card-header">
                <div className="card-title-group">
                  <input type="checkbox" checked={selectedIds.includes(a.id)} onClick={e => e.stopPropagation()} onChange={() => handleSelectOne(a.id)} />
                  <h4>{a.buyer} {a.archived && <span className="archived-tag">Archived</span>}</h4>
                </div>
                <span className="card-date">{new Date(a.date).toLocaleDateString()}</span>
              </div>
              <div className="card-body">
                <div className="card-item"><span className="label">Employee</span><span className="value">{a.employee_name}</span></div>
                <div className="card-item"><span className="label">Heads</span><span className="value">{formatNumber(a.heads)}</span></div>
                <div className="card-item"><span className="label">Weight</span><span className="value">{formatWeight(a.kilos)}</span></div>
                <div className="card-item">
                  <span className="label">Sale Tally</span>
                  <span className="value sale-link" onClick={(event) => { event.stopPropagation(); onSaleLinkClick(a.sale_id); }}>
                    {a.sale_tally}
                  </span>
                </div>
                <div className="card-item"><span className="label">Location</span><span className="value">{a.location_name || '-'}</span></div>
              </div>
              <div className="card-actions" onClick={e => e.stopPropagation()}>
                <button className="action-btn sm" onClick={() => handleEditClick(a)}>Edit</button>
                <button className="secondary-btn sm" onClick={() => handleArchive([a.id], !a.archived)}>{a.archived ? 'Restore' : 'Archive'}</button>
                {user.role === 'admin' && <button className="cancel-btn sm" onClick={() => handleDelete(a.id)}>Del</button>}
              </div>
            </div>
          ))}
          <div className="data-card summary-card">
            <h4>Totals Summary</h4>
            <div className="card-body">
              <div className="card-item"><span className="label">Total Records</span><span className="value">{formatNumber(filteredAgents.length)}</span></div>
              <div className="card-item"><span className="label">Total Heads</span><span className="value highlight">{formatNumber(totalHeads)}</span></div>
              <div className="card-item"><span className="label">Total Weight</span><span className="value">{formatWeight(totalKilos)}</span></div>
            </div>
          </div>
        </div>
      )}

      <Pagination 
        totalItems={filteredAgents.length} 
        itemsPerPage={rowsPerPage} 
        currentPage={currentPage} 
        onPageChange={setCurrentPage} 
      />

      {selectedIds.length > 0 && (
        <div className="bulk-action-bar no-print">
          <span className="selection-count">{selectedIds.length} items selected</span>
          <div className="bulk-actions">
            {filters.archived !== 'archived' && <button className="secondary-btn" onClick={() => handleArchive(selectedIds, true)}>Archive Selected</button>}
            {filters.archived !== 'unarchived' && <button className="submit-btn" onClick={() => handleArchive(selectedIds, false)}>Restore Selected</button>}
            <button className="cancel-btn" onClick={() => setSelectedIds([])}>Clear Selection</button>
          </div>
        </div>
      )}

      {showRecordModal && (
        <div className="modal" onClick={() => { setShowRecordModal(false); setEditingAgent(null); setError(''); }}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title-group">
                {editingAgent ? <Edit size={24} /> : <PlusCircle size={24} />}
                <h3>{editingAgent ? 'Edit Harvest Record' : 'Add Harvest Record'}</h3>
              </div>
              <button className="close-btn" onClick={() => { setShowRecordModal(false); setEditingAgent(null); setError(''); }}><X size={20} /></button>
            </div>
            {error && <div className="error-message">{error}</div>}
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Date</label>
                <input
                  name="date"
                  type="datetime-local"
                  value={editingAgent ? editingAgent.date : newAgent.date}
                  onChange={(e) => handleInputChange(e, !!editingAgent)}
                  required
                />
              </div>

              <div className="form-group">
                <label>Link to Sale (Required)</label>
                <input 
                  name="sale_search"
                  list="sale-list"
                  placeholder="Search sale tally..."
                  value={editingAgent ? editingAgent.sale_search : newAgent.sale_search}
                  onChange={(e) => handleInputChange(e, !!editingAgent)}
                  required
                />
                <datalist id="sale-list">
                  {sales.map(s => <option key={s.id} value={`${s.tally_sheet_number} - ${s.buyer}`} />)}
                </datalist>
              </div>

              <div className="form-group">
                <label>Employee (Required)</label>
                <input 
                  name="employee_search"
                  list="emp-list"
                  placeholder="Search employee..."
                  value={editingAgent ? editingAgent.employee_search : newAgent.employee_search}
                  onChange={(e) => handleInputChange(e, !!editingAgent)}
                  required
                />
                <datalist id="emp-list">
                  {employees.map(e => <option key={e.id} value={`${e.first_name} ${e.last_name}`} />)}
                </datalist>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Heads</label>
                  <input
                    name="heads"
                    type="number"
                    value={editingAgent ? editingAgent.heads : newAgent.heads}
                    onChange={(e) => handleInputChange(e, !!editingAgent)}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Kilos</label>
                  <input
                    name="kilos"
                    type="number"
                    step="0.01"
                    value={editingAgent ? editingAgent.kilos : newAgent.kilos}
                    onChange={(e) => handleInputChange(e, !!editingAgent)}
                    required
                  />
                </div>
              </div>

              {editingAgent && (
                <div className="form-group checkbox-group">
                  <label className="checkbox-label">
                    <input 
                      name="archived" 
                      type="checkbox" 
                      checked={editingAgent.archived} 
                      onChange={(e) => handleInputChange(e, true)} 
                    />
                    Archived
                  </label>
                </div>
              )}
              <div className="form-actions">
                <button type="submit" className="submit-btn" disabled={isSubmitting}>{isSubmitting ? 'Saving…' : (editingAgent ? 'Update Record' : 'Add Record')}</button>
                <button type="button" className="secondary-btn" onClick={() => { setShowRecordModal(false); setEditingAgent(null); setError(''); }}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {viewingAgent && (
        <div className="modal" onClick={() => setViewingAgent(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title-group">
                <Users size={24} />
                <h3>Harvest Record Details</h3>
              </div>
              <button className="close-btn" onClick={() => setViewingAgent(null)}><X size={20} /></button>
            </div>
            <div className="details-grid">
              <div className="details-column">
                <p><strong>Date:</strong> {new Date(viewingAgent.date).toLocaleDateString()}</p>
                <p><strong>Buyer:</strong> {viewingAgent.buyer}</p>
                <p><strong>Employee:</strong> {viewingAgent.employee_name}</p>
                <p><strong>Location:</strong> {viewingAgent.location_name || '-'}</p>
              </div>
              <div className="details-column">
                <p><strong>Heads:</strong> {viewingAgent.heads}</p>
                <p><strong>Weight:</strong> {viewingAgent.kilos.toFixed(2)} kg</p>
                <p><strong>Linked Sale:</strong> <span className="sale-link" onClick={() => onSaleLinkClick(viewingAgent.sale_id)}>{viewingAgent.sale_tally}</span></p>
              </div>
            </div>
            <div className="form-actions">
              <button className="secondary-btn" onClick={() => setViewingAgent(null)}>Close</button>
            </div>
          </div>
        </div>
      )}

      <ConfirmationModal
        isOpen={deleteConfirm.isOpen}
        title="Delete Harvest Record"
        message="Are you sure you want to delete this record? This will revert the harvested counts for the linked sale."
        onConfirm={confirmDelete}
        onCancel={() => setDeleteConfirm({ isOpen: false, id: null })}
      />

      <ConfirmationModal
        isOpen={archiveConfirm.isOpen}
        title={archiveConfirm.archived ? 'Archive Records' : 'Restore Records'}
        message={(archiveConfirm.archived
          ? 'Archive '
          : 'Restore ') + (archiveConfirm.ids.length === 1
            ? 'this record?'
            : `these ${archiveConfirm.ids.length} records?`)}
        confirmText={archiveConfirm.archived ? 'Archive' : 'Restore'}
        confirmClass={archiveConfirm.archived ? 'secondary-btn' : 'submit-btn'}
        onConfirm={performArchive}
        onCancel={() => setArchiveConfirm({ isOpen: false, ids: [], archived: false })}
      />
    </div>
  );
}

export default HarvestAgents;
